import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { INDIAN_BRANDS, GLOBAL_BRANDS } from "@/data/products";
import { Reveal } from "@/components/Reveal";

function Cell({ name, tag, index, group, onClick, testId, logo }) {
  return (
    <motion.button
      data-testid={testId}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      onClick={onClick}
      className="group relative flex min-h-[150px] flex-col items-start justify-between bg-pine p-6 text-left transition-colors duration-500 hover:bg-moss sm:min-h-[170px] sm:p-8"
    >
      <div className="flex w-full items-center justify-between">
        <span className="font-mono text-[9px] tracking-[0.3em] text-fern">{group}.{String(index + 1).padStart(2, "0")}</span>
        <ArrowUpRight className="h-4 w-4 text-gold opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100" />
      </div>
      <div>
        {logo ? (
          <img src={logo} alt={name} className="max-h-12 max-w-[80%] object-contain object-left mix-blend-multiply" data-testid={`${testId}-logo`} />
        ) : (
          <h3 className="font-display text-xl tracking-tight text-cream transition-colors duration-300 group-hover:text-gold sm:text-3xl">{name}</h3>
        )}
        <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-fern">{tag}</p>
      </div>
    </motion.button>
  );
}

const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export default function BrandIndex({ data }) {
  const { catalog } = useStore();
  const eyebrow = data?.eyebrow || "The Brand Index";
  const navigate = useNavigate();
  const live = catalog.brands || [];

  return (
    <section id="brands" data-testid="brands-section" className="border-t border-champagne/40 bg-pine py-10 sm:py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">{eyebrow}</p>
              <h2 className="mt-3 max-w-xl font-display text-3xl leading-tight tracking-tight text-cream sm:text-5xl">
                {data?.heading || (<>Every house, <span className="italic text-gold">one address</span></>)}
              </h2>
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-fern">
              Every maison is hand-audited for authenticity before it earns a place on our shelf.
            </p>
          </div>
        </Reveal>

        {live.length > 0 ? (
          <div className="mt-8">
            <Reveal className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="flex items-baseline gap-3 font-display text-2xl text-cream sm:text-3xl">
                <span className="font-mono text-xs text-gold">01</span> The Directory
              </h3>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-fern">{live.length} houses · live from HQ</p>
            </Reveal>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-champagne/50 bg-champagne/50 lg:grid-cols-3">
              {live.map((b, i) => (
                <Cell
                  key={b.id}
                  name={titleCase(b.name)}
                  tag={b.product_count ? `${b.product_count} products` : "Stocking soon"}
                  index={i}
                  group="01"
                  logo={b.logo_url}
                  testId={`brand-cell-${b.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                  onClick={() => navigate(`/shop?brand_id=${b.id}`)}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            {[{ no: "01", label: "Made in India", note: "Legacy & Disruptors", items: INDIAN_BRANDS }, { no: "02", label: "World Stage", note: "Global Icons", items: GLOBAL_BRANDS }].map((g) => (
              <div key={g.no} className="mt-8">
                <Reveal className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="flex items-baseline gap-3 font-display text-2xl text-cream sm:text-3xl">
                    <span className="font-mono text-xs text-gold">{g.no}</span> {g.label}
                  </h3>
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-fern">{g.note}</p>
                </Reveal>
                <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-champagne/50 bg-champagne/50 lg:grid-cols-3">
                  {g.items.map((b, i) => (
                    <Cell key={b.name} name={b.name} tag={b.tag} index={i} group={g.no} testId={`brand-cell-${b.name.replace(/[^a-z]/gi, "").toLowerCase()}`} onClick={() => navigate("/shop")} />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );
}
